import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { FrameworkSnapshot } from "@apf/framework-config";
import type { SearchIndexArtifact } from "@apf/framework-config/search-index";
import type { FrameworkDataProvider } from "@apf/workblock-adapter";
import { StaticFrameworkProvider } from "@apf/workblock-adapter";
import embeddedSnapshotJson from "../../../framework/generated/accenture-site.snapshot.json";

const embeddedSnapshot = embeddedSnapshotJson as unknown as FrameworkSnapshot;

export class ApiFrameworkProvider implements FrameworkDataProvider {
  constructor(private readonly baseUrl = "") {}

  async getFrameworkSnapshot(): Promise<FrameworkSnapshot> {
    const response = await fetch(`${this.baseUrl}/api/v1/snapshot`, { credentials: "include" });
    if (!response.ok) throw new Error(response.status === 401 ? "Access key required" : "Framework snapshot could not be loaded");
    return response.json() as Promise<FrameworkSnapshot>;
  }

  /**
   * The generated per-audience search artifact. Fetched only when the
   * /search page mounts — the artifact is never a static import, so the
   * corpus stays out of the main bundle — and the server selects the
   * audience from the authenticated viewer alone. A refusal here is the
   * page's cue to fall back to its in-browser snapshot index.
   */
  async getSearchIndex(): Promise<SearchIndexArtifact> {
    const response = await fetch(`${this.baseUrl}/api/v2/search-index`, { credentials: "include" });
    if (!response.ok) throw new Error(response.status === 401 ? "Access key required" : "Search index could not be loaded");
    return response.json() as Promise<SearchIndexArtifact>;
  }
}

interface FrameworkContextValue {
  snapshot: FrameworkSnapshot;
  provider: FrameworkDataProvider;
  refresh: () => Promise<void>;
}

const FrameworkContext = createContext<FrameworkContextValue | undefined>(undefined);

export interface FrameworkProviderProps {
  children: ReactNode;
  provider?: FrameworkDataProvider;
  initialSnapshot?: FrameworkSnapshot;
  /**
   * Wait for the supplied provider before mounting snapshot consumers. The
   * portable Workblock uses this path so its browser bundle does not need a
   * second copy of the generated site projection.
   */
  deferUntilProvider?: boolean;
}

type FrameworkLoadState = {
  snapshot?: FrameworkSnapshot;
  error?: string;
};

const frameworkLoadError =
  "The published framework data did not arrive. Try again. If it remains unavailable, reopen the Workblock. No work has been changed.";

function FrameworkLoadingState() {
  return (
    <div className="apf-loading-state" role="status" aria-live="polite">
      <div className="apf-state-message">
        <h2>Loading Accenture GroundWorks…</h2>
        <div className="apf-loading-state__bar" aria-hidden="true" />
      </div>
    </div>
  );
}

function FrameworkRecoveryState({ retry }: { retry: () => void }) {
  return (
    <div className="apf-error-state" role="alert" aria-live="assertive">
      <div className="apf-state-message">
        <h2>Framework snapshot unavailable</h2>
        <p>{frameworkLoadError}</p>
        <div className="apf-empty-state__action">
          <button className="apf-button" type="button" onClick={retry}>
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

export function FrameworkProvider({
  children,
  provider,
  initialSnapshot,
  deferUntilProvider = false,
}: FrameworkProviderProps) {
  const fallbackSnapshot = initialSnapshot ?? embeddedSnapshot;
  const waitsForProvider = Boolean(provider && deferUntilProvider);
  const resolvedProvider = useMemo<FrameworkDataProvider>(
    () => provider ?? new StaticFrameworkProvider(fallbackSnapshot),
    [provider, fallbackSnapshot]
  );
  const requestGeneration = useRef(0);
  const [loadState, setLoadState] = useState<FrameworkLoadState>(() =>
    waitsForProvider ? {} : { snapshot: fallbackSnapshot },
  );

  const loadFromProvider = useCallback(
    async ({ showLoading, keepLastGood }: { showLoading: boolean; keepLastGood: boolean }) => {
      const generation = ++requestGeneration.current;
      if (showLoading) setLoadState({});
      try {
        const next = await resolvedProvider.getFrameworkSnapshot();
        if (generation === requestGeneration.current) setLoadState({ snapshot: next });
      } catch (error) {
        if (generation === requestGeneration.current) {
          setLoadState((current) =>
            keepLastGood && current.snapshot
              ? current
              : { error: frameworkLoadError },
          );
        }
        throw error;
      }
    },
    [resolvedProvider],
  );

  const refresh = useCallback(
    () => loadFromProvider({ showLoading: false, keepLastGood: true }),
    [loadFromProvider],
  );

  const retry = useCallback(() => {
    void loadFromProvider({ showLoading: true, keepLastGood: false }).catch(() => undefined);
  }, [loadFromProvider]);

  useEffect(() => {
    if (!provider) {
      setLoadState({ snapshot: fallbackSnapshot });
      return;
    }

    void loadFromProvider({
      showLoading: deferUntilProvider,
      keepLastGood: !deferUntilProvider,
    }).catch((error: unknown) => {
      if (!deferUntilProvider) {
        console.warn("Framework snapshot refresh failed; showing the embedded snapshot.", error);
      }
    });

    return () => {
      requestGeneration.current += 1;
    };
  }, [deferUntilProvider, fallbackSnapshot, loadFromProvider, provider]);

  if (!loadState.snapshot) {
    return loadState.error
      ? <FrameworkRecoveryState retry={retry} />
      : <FrameworkLoadingState />;
  }

  return (
    <FrameworkContext.Provider value={{ snapshot: loadState.snapshot, provider: resolvedProvider, refresh }}>
      {children}
    </FrameworkContext.Provider>
  );
}

export function useFramework(): FrameworkContextValue {
  const context = useContext(FrameworkContext);
  if (!context) throw new Error("useFramework must be used within FrameworkProvider");
  return context;
}

export function useCopy() {
  const { snapshot } = useFramework();
  return (key: string, fallback?: string) => snapshot.copy[key] ?? fallback ?? key;
}

export { embeddedSnapshot };
